/**
 * "I want to learn this word."
 *
 * Both children asked for this, and it is the only box in the app where what
 * a child types decides what they are taught. So the screen is honest about
 * what happens next, which turns out to matter more than any of the checking.
 *
 * Three answers, and the child is told which one they got:
 *
 *   **In it goes.** The app already knew the word, so it is in the pool
 *   before they let go of the button. This is most of what a child types —
 *   dinosaur, volcano, lightning — and it is what makes the feature feel like
 *   it belongs to them.
 *
 *   **A grown-up will look.** The word is new to the app. Nothing has gone
 *   wrong and nobody is in trouble; it is simply waiting.
 *
 *   **Not for this app.** A check said no. The sentence is the same whichever
 *   check caught it, and it points at a grown-up rather than at the child.
 *
 * What is NOT here is anything that reads like a telling-off. A child who
 * types a word they heard in a playground and gets a red warning learns to
 * stop typing, not to stop wondering. The server writes every request down,
 * including that one, so the conversation can happen with a person.
 */
import { useEffect, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import { api, type WordRequest } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'
import { Shell } from '../components/Shell'
import { inputStyle } from '../components/ui'
import { Trace, TraceRegion } from '../lib/trace'

const LOOKS = {
  added: { emoji: '🌱', tone: 'green', line: 'It is in your words now.' },
  waiting: { emoji: '⏳', tone: 'orange', line: 'A grown-up will have a look.' },
  blocked: { emoji: '🙅', tone: 'grey', line: 'Not one for this app.' },
  declined: { emoji: '🙅', tone: 'grey', line: 'A grown-up said not this one.' },
} as const

export function AskForWord({
  learnerId, onHome, onGarden,
}: { learnerId: string; onHome: () => void; onGarden: () => void }) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [mine, setMine] = useState<WordRequest[]>([])
  const [justAsked, setJustAsked] = useState<WordRequest | null>(null)

  const load = () => {
    api.myWordRequests(learnerId)
      .then(r => setMine(r.requests))
      .catch(() => undefined)
  }
  useEffect(load, [learnerId])

  const send = async () => {
    const word = typed.trim()
    if (!word || busy) return
    setBusy(true)
    setProblem(null)
    try {
      const made = await api.askForWord(learnerId, word, 'child')
      setJustAsked(made)
      setTyped('')
      load()
    } catch (err) {
      // A 400 means the request could not be made at all — a sentence, a
      // word they already have, a word no dictionary knows. The server
      // writes those messages for a child to read, so they are shown as
      // they are rather than turned into "something went wrong".
      setProblem(messageFrom(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page width={560}>
      <Shell onExit={onHome}>
        <Heading sub="Heard a word you liked? Tell us and we will teach it to you.">
          Ask for a word
        </Heading>

        <TraceRegion id="T22" note="every ask is written down, answered or not">
        <Card>
          <input
            style={{ ...inputStyle, fontSize: 22, fontWeight: 700 }}
            value={typed}
            onChange={(e) => { setTyped(e.target.value); setProblem(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') void send() }}
            placeholder="One word"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            maxLength={30}
          />
          {problem ? (
            <p style={{ margin: '12px 0 0', fontSize: 16, color: theme.orangeDark,
                        lineHeight: 1.5 }}>
              {problem}
            </p>
          ) : null}
          <div style={{ marginTop: 18 }}>
            <Button full tone="child" disabled={!typed.trim() || busy}
                    onClick={() => void send()}>
              {busy ? 'Looking it up…' : 'Ask for it'}
            </Button>
          </div>
        </Card>
        </TraceRegion>

        {/* The answer, said plainly and straight away. */}
        {justAsked ? (
          <Answer request={justAsked} onGarden={onGarden} />
        ) : null}

        {mine.length ? (
          <div style={{ marginTop: 24 }}>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: theme.text,
                         margin: '0 0 10px' }}>
              Words you asked for
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {mine.map(r => <Row key={r.request_id} request={r} />)}
            </div>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
          <Button tone="quiet" onClick={onGarden}>My garden</Button>
          <ExitButton onExit={onHome} />
        </div>
      </Shell>
    </Page>
  )
}


/** The big one, right after asking. */
function Answer({
  request, onGarden,
}: { request: WordRequest; onGarden: () => void }) {
  const look = LOOKS[request.status]
  const green = request.status === 'added'
  return (
    <Card style={{
      marginTop: 16,
      borderColor: green ? theme.green : theme.border,
      background: green ? theme.greenPale : theme.card,
    }}>
      <div style={{ fontSize: 32, marginBottom: 6 }}>{look.emoji}</div>
      <div style={{ fontSize: 20, fontWeight: 800,
                    color: green ? theme.greenDarker : theme.text }}>
        {request.asked}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 17, color: theme.text2,
                  lineHeight: 1.5 }}>
        {request.note || look.line}
      </p>
      {/* Only once the word is in the child's list. A waiting word's meaning
          came straight from an online dictionary and no grown-up has read it
          yet (audit, 12 Oct 2026). */}
      {request.definition && request.status === 'added' ? (
        <p style={{ margin: '10px 0 0', fontSize: 16, color: theme.muted,
                    lineHeight: 1.5 }}>
          <Trace id="T22.definition">{request.definition}</Trace>
        </p>
      ) : null}
      {green ? (
        <div style={{ marginTop: 14 }}>
          <Button tone="quiet" onClick={onGarden}>See it in my garden</Button>
        </div>
      ) : null}
    </Card>
  )
}


/** One line of the history, so asking never disappears without trace. */
function Row({ request }: { request: WordRequest }) {
  const look = LOOKS[request.status]
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', minHeight: TAP_MIN,
      background: theme.card, border: `1px solid ${theme.border}`,
      borderRadius: radius.md,
    }}>
      <span style={{ fontSize: 20 }}>{look.emoji}</span>
      <span style={{ fontWeight: 700, color: theme.text, fontSize: 16 }}>
        <Trace id="T22.asked">{request.asked}</Trace>
      </span>
      <span style={{ marginLeft: 'auto', fontSize: 14, color: theme.muted,
                     textAlign: 'right' }}>
        <Trace id="T22.status">{look.line}</Trace>
      </span>
    </div>
  )
}


function messageFrom(err: unknown): string {
  const detail = (err as { detail?: unknown })?.detail
  if (typeof detail === 'string' && detail) return detail
  const message = (err as { message?: unknown })?.message
  if (typeof message === 'string' && message) return message
  return 'That did not work. Try again in a moment.'
}
